import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { StatsSection } from "@/components/home/StatsSection";
import { Testimonials } from "@/components/home/Testimonials";
import { SERVICES, STEPS, IMAGES } from "@/data/content";
import { ArrowRight, Check } from "lucide-react";

const Label = ({ children, className = "" }) => (
    <span className={`font-mono text-[11px] uppercase tracking-[0.24em] ${className}`}>
        {children}
    </span>
);

export default function Services() {
    return (
        <div data-testid="services-page" className="pt-[76px]">
            <Seo
                title="Dental Billing & Revenue Cycle Services in Texas | DentAssists"
                description="DentAssists services for Texas dental practices: insurance verification, claims submission, payment posting, AR & aging management, provider credentialing, and reporting — inside your existing software."
                path="/services"
            />
            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-14">
                <Reveal>
                    <Label className="text-terracotta">Services</Label>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tight text-ink mt-6 max-w-3xl leading-[1]">
                        Everything between the appointment and the payment.
                    </h1>
                    <p className="text-base lg:text-lg text-inksoft leading-relaxed mt-7 max-w-2xl">
                        We work inside Dentrix, Eaglesoft, Open Dental, and more — so there's nothing
                        new for your staff to learn. Pick the full package or just the pieces you
                        need.
                    </p>
                </Reveal>
            </section>

            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pb-10">
                <div className="grid lg:grid-cols-2 border-t border-l border-linec">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <Reveal key={s.title} delay={(i % 2) * 0.08}>
                                <div className="group border-r border-b border-linec p-9 lg:p-12 h-full transition-colors duration-300 hover:bg-stone2/60">
                                    <div className="flex items-start justify-between">
                                        <Icon size={30} className="text-sage" strokeWidth={1.5} />
                                        <span className="font-heading text-3xl text-linec">
                                            0{i + 1}
                                        </span>
                                    </div>
                                    <h3 className="font-heading text-2xl lg:text-3xl text-ink mt-6">
                                        {s.title}
                                    </h3>
                                    <p className="text-base text-inksoft leading-relaxed mt-4 max-w-md">
                                        {s.blurb}
                                    </p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <StatsSection />

            <section className="bg-stone2/50 border-y border-linec">
                <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20">
                    <Reveal>
                        <Label className="text-terracotta">How onboarding works</Label>
                        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink mt-4">
                            Live in four steps.
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {STEPS.map((step, i) => (
                            <Reveal key={step.n} delay={i * 0.1}>
                                <div className="bg-cream border border-linec rounded-sm p-8 h-full">
                                    <div className="font-heading text-5xl text-terracotta/90">{step.n}</div>
                                    <h3 className="font-heading text-xl text-ink mt-5">{step.title}</h3>
                                    <p className="text-sm text-inksoft leading-relaxed mt-3">{step.blurb}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative">
                <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
                    <Reveal>
                        <img
                            src={IMAGES.services}
                            alt="Modern dental clinic interior"
                            className="w-full h-[400px] object-cover rounded-sm"
                        />
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-heading text-4xl tracking-tight text-ink max-w-md">
                            Outsource one service, or all of them.
                        </h2>
                        <ul className="mt-8 space-y-4">
                            {["Verification only", "Billing only", "Full revenue-cycle package"].map(
                                (o) => (
                                    <li key={o} className="flex items-center gap-3 text-inksoft">
                                        <Check size={18} className="text-terracotta shrink-0" />
                                        {o}
                                    </li>
                                )
                            )}
                        </ul>
                        <Link
                            to="/contact"
                            data-testid="services-cta-button"
                            className="inline-flex items-center gap-2 bg-terracotta text-cream px-8 py-4 rounded-full font-medium mt-9 transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5 active:scale-95"
                        >
                            Get a Free Consultation
                            <ArrowRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            <Testimonials />
        </div>
    );
}
